import type { Option } from '@cf/shared'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { cn } from '@/lib/utils'

/**
 * A single-choice answer row.
 *
 * Built on ToggleGroup rather than a loop of buttons with hand-rolled active
 * state, which also buys arrow-key navigation and the right ARIA roles.
 *
 * Hover is grey and selection carries the colour (owner, 2026-08-03). The
 * generated toggle hovers to `accent`, which in this theme is the green, so
 * every option lit up green under the cursor and looked chosen before it was.
 * Colour now means "this is my answer", and the tone says which answer.
 *
 * `spacing` is set for the same reason. At its default of 0 the group is one
 * joined bar and every item after the first has `border-l-0`, sharing its
 * neighbour's edge; a coloured border on the second or third option then came
 * out with the left side missing. Separated options each own all four edges.
 */
const HOVER = 'hover:bg-secondary hover:text-foreground'

const SELECTED = {
  green: 'data-[state=on]:border-green data-[state=on]:bg-green-light data-[state=on]:text-green-dark',
  amber: 'data-[state=on]:border-amber data-[state=on]:bg-amber-light data-[state=on]:text-amber-dark',
  red: 'data-[state=on]:border-clay data-[state=on]:bg-clay-light data-[state=on]:text-clay-dark',
  neutral: 'data-[state=on]:border-ink-soft data-[state=on]:bg-secondary data-[state=on]:text-foreground',
} as const

export function Choice({
  options,
  value,
  onChange,
  className,
  ariaLabel,
}: {
  options: readonly Option[]
  value: string | null | undefined
  onChange: (value: string | null) => void
  className?: string
  ariaLabel?: string
}) {
  return (
    <ToggleGroup
      type="single"
      variant="outline"
      spacing={2}
      value={value ?? ''}
      // Clicking the chosen option again clears it: an accidental click should
      // be undoable, and "no answer" is a real state on every question but the
      // first.
      onValueChange={(next) => onChange(next === '' ? null : next)}
      aria-label={ariaLabel}
      className={cn('flex w-full flex-wrap', className)}
    >
      {options.map((option) => (
        <ToggleGroupItem
          key={option.value}
          value={option.value}
          className={cn(
            'flex-1 data-[state=on]:font-semibold',
            HOVER,
            SELECTED[option.tone],
          )}
        >
          {option.label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  )
}
